<template>
  <div class="widget-generator">
    <div class="widget-header">
      <h2 class="section-title">Widget supplyvet</h2>

      <p class="section-description">
        Mise en place d'un widget sur le site internet d'une clinique
      </p>

      <div id="supplyvet-widget-custom">
        <a href="#" target="_blank" class="supplyvet-widget-content">
          <img :src="logo" alt="Supplyvet">
        </a>
        <div class="supplyvet-widget-hover">
          <a href="#">
            <p>POUR COMMANDER <br>
              <strong>CLIQUEZ ICI !</strong>
            </p>
          </a>
        </div>
      </div>
    </div>
    <p class="section-description">
      Module permettant de générer le code d'intégration du widget à fournir à la clinique pour afficher le widget sur
      son site internet.
      La clinique devra le mettre en place soit via CMS(Wordpress, Joomla, etc...), sinon, faire appel à son prestataire
      informatique.
    </p>

    <div class="form-container">
      <div class="form-group">
        <label for="veterinarian">Vétérinaire *</label>
        <div class="searchable-select">
          <div class="input-wrapper">
            <input
              type="text"
              v-model="searchQuery"
              :placeholder="selectedVeterinarian ? '' : 'Rechercher un vétérinaire...'"
              class="form-control search-input"
              :class="{ 'is-invalid': errors.veterinarian, 'has-value': selectedVeterinarian }"
              @focus="showDropdown = true"
              @input="handleSearchInput"
              @keydown.enter="handleEnterKey"
              @keydown.escape="showDropdown = false"
            />
            <span v-if="selectedVeterinarian" class="clear-btn" @click="clearSelection">×</span>
          </div>
          <div class="dropdown" v-show="showDropdown">
            <div v-if="filteredVeterinarians.length" class="dropdown-list">
              <div
                v-for="(vet, index) in filteredVeterinarians"
                :key="vet.id"
                class="dropdown-item"
                :class="{ 'highlighted': highlightedIndex === index }"
                @mousedown.prevent="selectVeterinarian(vet)"
                @mouseenter="highlightedIndex = index"
              >
                <span class="vet-name">{{ vet.fullName }}</span>
                <span class="vet-id">({{ vet.divalto_id }})</span>
              </div>
            </div>
            <div v-else class="dropdown-item no-results">
              Aucun vétérinaire trouvé pour "{{ searchQuery }}"
            </div>
          </div>
        </div>
        <div v-if="errors.veterinarian" class="invalid-feedback">
          {{ errors.veterinarian }}
        </div>
      </div>

      <div class="form-group">
        <label>Position du widget *</label>
        <div class="position-options">
          <div v-for="position in positions" :key="position.value" class="position-option"
            :class="{ active: selectedPosition === position.value }" @click="selectedPosition = position.value">
            <div class="position-preview">
              <div class="widget-placeholder" :class="position.widgetClass"></div>
            </div>
            <span class="position-label">{{ position.label }}</span>
          </div>
        </div>
        <div v-if="errors.position" class="invalid-feedback">
          {{ errors.position }}
        </div>
      </div>

      <div class="form-group">
        <button class="btn btn-primary generate-btn" @click="generateWidgetCode" :disabled="isGenerating || !selectedVeterinarian">
          <span v-if="isGenerating" class="spinner-border spinner-border-sm" role="status"></span>
          {{ isGenerating ? 'Génération...' : 'Générer le code' }}
        </button>
      </div>
    </div>

    <div v-if="widgetCode" class="code-result">
      <h3>Code d'intégration</h3>
      <div class="code-container">
        <pre><code>{{ widgetCode }}</code></pre>
        <button class="btn btn-copy" @click="copyToClipboard" :class="{ 'copied': isCopied }">
          <span v-if="!isCopied">Copier</span>
          <span v-else>Copié !</span>
        </button>
      </div>

      <div class="direct-link-section" v-if="directLink">
        <h3>Lien direct</h3>
        <div class="link-container">
          <a :href="directLink" target="_blank" class="direct-link">{{ directLink }}</a>
          <button class="btn btn-copy" @click="copyLinkToClipboard" :class="{ 'copied': isLinkCopied }">
            <span v-if="!isLinkCopied">Copier</span>
            <span v-else>Copié !</span>
          </button>
        </div>
        <p class="link-description">
          Ce lien permet d'accéder directement au widget sans intégration.
        </p>
      </div>

      <div class="instructions">
        <h4>Instructions d'intégration :</h4>
        <ol>
          <li>Copiez le code ci-dessus</li>
          <li>Collez-le dans le code HTML de votre site vétérinaire</li>
          <li>Placez-le à l'endroit souhaité selon la position sélectionnée</li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script>
import { getVeterinarians } from "@/api/Delegate";
import logo from "../assets/images/widget_delegue.png";

export default {
  name: "WidgetSupplyvet",
  data() {
    return {
      veterinarians: [],
      selectedVeterinarian: null,
      searchQuery: "",
      showDropdown: false,
      highlightedIndex: -1,
      selectedPosition: "bottom-right",
      widgetCode: "",
      directLink: "",
      isGenerating: false,
      isCopied: false,
      isLinkCopied: false,
      errors: {
        veterinarian: "",
        position: ""
      },
      positions: [
        {
          value: "top-left",
          label: "En haut à gauche",
          widgetClass: "top-left"
        },
        {
          value: "top-right",
          label: "En haut à droite",
          widgetClass: "top-right"
        },
        {
          value: "bottom-left",
          label: "En bas à gauche",
          widgetClass: "bottom-left"
        },
        {
          value: "bottom-right",
          label: "En bas à droite",
          widgetClass: "bottom-right"
        },
      ],
      logo
    };
  },
  computed: {
    filteredVeterinarians() {
      if (!this.searchQuery.trim()) return this.veterinarians;
      
      const query = this.searchQuery.toLowerCase().trim();
      return this.veterinarians.filter(
        vet =>
          vet.fullName.toLowerCase().includes(query) ||
          vet.divalto_id.toLowerCase().includes(query)
      );
    }
  },
  watch: {
    searchQuery(newQuery) {
      // Reset highlight when search changes
      this.highlightedIndex = newQuery ? 0 : -1;
    },
    showDropdown(isVisible) {
      if (isVisible) {
        this.highlightedIndex = this.searchQuery ? 0 : -1;
      }
    }
  },
  async mounted() {
    await this.fetchVeterinarians();
  },
  methods: {
    async fetchVeterinarians() {
      console.log("Fetching veterinarians...from widget");
      try {
        this.veterinarians = await getVeterinarians();
      } catch (error) {
        console.error("Erreur lors du chargement des vétérinaires:", error);
      }
    },
    
    handleSearchInput() {
      this.showDropdown = true;
    },
    
    handleEnterKey() {
      if (this.highlightedIndex >= 0 && this.filteredVeterinarians[this.highlightedIndex]) {
        this.selectVeterinarian(this.filteredVeterinarians[this.highlightedIndex]);
      }
      this.showDropdown = false;
    },
    
    selectVeterinarian(vet) {
      this.selectedVeterinarian = vet;
      this.searchQuery = vet.fullName;
      this.showDropdown = false;
      this.highlightedIndex = -1;
      this.errors.veterinarian = "";
    },
    
    clearSelection() {
      this.selectedVeterinarian = null;
      this.searchQuery = "";
      this.showDropdown = true;
      this.highlightedIndex = -1;
    },
    
    hideDropdown() {
      setTimeout(() => {
        this.showDropdown = false;
        this.highlightedIndex = -1;
      }, 150);
    },

    validateForm() {
      this.errors = {
        veterinarian: "",
        position: ""
      };

      let isValid = true;

      if (!this.selectedVeterinarian) {
        this.errors.veterinarian = "Veuillez sélectionner un vétérinaire";
        isValid = false;
      }

      if (!this.selectedPosition) {
        this.errors.position = "Veuillez sélectionner une position";
        isValid = false;
      }

      return isValid;
    },
    
    generateWidgetCode() {
  if (!this.validateForm()) return;

  this.isGenerating = true;
  this.isCopied = false;
  this.isLinkCopied = false;

  const baseURL = process.env.VUE_APP_FRONT_URL;
  const widgetScriptURL = `${baseURL}widget/script/widget.js`;

  setTimeout(() => {
    const vet = this.selectedVeterinarian;
    const position = this.selectedPosition;
    
    const positionMap = {
      "top-left": "hautGauche",
      "top-right": "hautDroite",
      "bottom-left": "basGauche",
      "bottom-right": "basDroite"
    };
    
    const frenchPosition = positionMap[position];
    
    this.widgetCode = `<script type="text/javascript" src="${widgetScriptURL}" id="supplyvet-widget"></${'script'}>
    <script type="text/javascript">
    window.onload = function() {
      supplyvetWidget.setOptions({
          position: "${frenchPosition}",
          token: "${vet.divalto_id}",
          baseUrl: "${baseURL}" 
      });
      supplyvetWidget.render();
    }
    </${'script'}>`;
    
    this.directLink = `${baseURL}widget?token=${vet.divalto_id}&position=${frenchPosition}`;
    
    this.isGenerating = false;
  }, 800);
},
    
    copyToClipboard() {
      navigator.clipboard.writeText(this.widgetCode)
        .then(() => {
          this.isCopied = true;
          setTimeout(() => {
            this.isCopied = false;
          }, 2000);
        })
        .catch(err => {
          console.error('Erreur lors de la copie: ', err);
        });
    },
    
    copyLinkToClipboard() {
      navigator.clipboard.writeText(this.directLink)
        .then(() => {
          this.isLinkCopied = true;
          setTimeout(() => {
            this.isLinkCopied = false;
          }, 2000);
        })
        .catch(err => {
          console.error('Erreur lors de la copie du lien: ', err);
        });
    }
  }
};
</script>

<style scoped>
.widget-generator {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}

.section-title {
  color: #1e347d;
  font-size: 24px;
  margin-bottom: 10px;
}

.section-description {
  color: #515a6e;
  margin-bottom: 30px;
}

.form-container {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 25px;
  margin-bottom: 30px;
}

.form-group {
  margin-bottom: 25px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
}

.searchable-select {
  position: relative;
}

.input-wrapper {
  position: relative;
}

.search-input {
  width: 100%;
  padding: 10px 15px;
  padding-right: 35px;
  border: 1px solid #ced4da;
  border-radius: 20px;
  font-size: 16px;
  transition: all 0.2s ease;
}

.search-input:focus {
  border-color: #1e347d;
  box-shadow: 0 0 0 0.2rem rgba(30, 52, 125, 0.25);
  outline: none;
}

.search-input.has-value {
  background-color: #f8f9fa;
  font-weight: 500;
}

.clear-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 20px;
  color: #6c757d;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.clear-btn:hover {
  background-color: #e9ecef;
  color: #495057;
}

.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 0 0 20px 20px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  border-top: none;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.dropdown-list {
  padding: 4px 0;
}

.dropdown-item {
  padding: 10px 15px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.15s ease;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dropdown-item:hover,
.dropdown-item.highlighted {
  background: #eef2ff;
  color: #1e347d;
}

.vet-name {
  font-weight: 500;
}

.vet-id {
  color: #6c757d;
  font-size: 12px;
}

.dropdown-item:hover .vet-id,
.dropdown-item.highlighted .vet-id {
  color: #1e347d;
}

.dropdown-item.no-results {
  color: #6c757d;
  cursor: default;
  font-style: italic;
}

.dropdown-item.no-results:hover {
  background: transparent;
  color: #6c757d;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #dc3545;
}

.position-options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
}

.position-option {
  cursor: pointer;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  padding: 15px;
  text-align: center;
  transition: all 0.2s ease;
}

.position-option:hover,
.position-option.active {
  border-color: #1e347d;
  background-color: #eef2ff;
}

.position-preview {
  height: 80px;
  position: relative;
  margin-bottom: 10px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.widget-placeholder {
  position: absolute;
  width: 40px;
  height: 40px;
  background: #1e347d;
  border-radius: 4px;
}

.widget-placeholder.top-left {
  top: 10px;
  left: 10px;
}

.widget-placeholder.top-right {
  top: 10px;
  right: 10px;
}

.widget-placeholder.bottom-left {
  bottom: 10px;
  left: 10px;
}

.widget-placeholder.bottom-right {
  bottom: 10px;
  right: 10px;
}

.position-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.generate-btn {
  background-color: #1e347d;
  border-color: #1e347d;
  padding: 10px 25px;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.generate-btn:hover:not(:disabled) {
  background-color: #172a63;
  border-color: #172a63;
}

.generate-btn:disabled {
  background-color: #6c757d;
  border-color: #6c757d;
  cursor: not-allowed;
}

.code-result {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 25px;
}

.code-result h3 {
  color: #1e347d;
  margin-bottom: 20px;
}

.code-container,
.link-container {
  position: relative;
  margin-bottom: 25px;
}

pre {
  background: #2d2d2d;
  color: #f8f8f2;
  padding: 20px;
  border-radius: 4px;
  overflow-x: auto;
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
}

.direct-link {
  display: block;
  background: #e9ecef;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 10px;
  word-break: break-all;
  color: #1e347d;
  text-decoration: none;
}

.direct-link:hover {
  text-decoration: underline;
}

.btn-copy {
  position: absolute;
  top: 15px;
  right: 15px;
  background: #444;
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-copy:hover {
  background: #555;
}

.btn-copy.copied {
  background: #28a745;
}

.link-description {
  font-size: 14px;
  color: #6c757d;
  margin-top: 10px;
}

.instructions h4 {
  color: #1e347d;
  margin-bottom: 15px;
}

.instructions ol {
  padding-left: 20px;
  margin-bottom: 0;
}

.instructions li {
  margin-bottom: 10px;
}

.spinner-border {
  margin-right: 8px;
}

.widget-header {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.supplyvet-widget-hover {
  background-color: #1e347d;
  display: flex;
  justify-content: center;
  margin-top: 20px;
  margin-bottom: 30px;
  transition: transform .8s cubic-bezier(.19, 1, .22, 1), opacity 0.3s ease;
  transform: none;
  width: 100%;
  border-radius: 14px;
  padding: 12px 0 0 0;
}

a {
  color: #e9ecef;
}

.supplyvet-widget-content {
  box-shadow: 0 4px 50px 0 rgb(0 0 0 / 18%);
  border-radius: 14px;
  opacity: 1;
  cursor: pointer;
  padding: 20px;
  background: #fff;
}

@media (max-width: 768px) {
  .position-options {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .widget-generator {
    padding: 15px;
  }
  
  .form-container,
  .code-result {
    padding: 20px;
  }
  
  pre {
    font-size: 12px;
    padding: 15px;
  }
}

@media (max-width: 576px) {
  .position-options {
    grid-template-columns: 1fr;
  }
  
  .section-title {
    font-size: 20px;
  }
  
  .form-container,
  .code-result {
    padding: 15px;
  }
  
  .dropdown-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .vet-id {
    font-size: 11px;
  }
}
</style>